"use client";

import { useState } from "react";
import {
  type BarCategory,
  BarChart,
} from "@/components/tool-kit/charts/bar-chart";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import {
  computeRoi,
  type RoiResult,
  rankRois,
} from "@/lib/tool-kit/finance/roi";

/** English strings. A translated content file overrides these through `ui`. */
export const ROI_CALCULATOR_STRINGS = {
  heading: "Your investments",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  namePlaceholder: "Investment {n}",
  nameLabel: "Name of investment {n}",
  invested: "Amount invested",
  finalValue: "Value at the end",
  income: "Income received (dividends, rent, interest)",
  costs: "Fees and other costs",
  years: "Holding period (years)",
  remove: "Remove",
  removeLabel: "Remove investment {n}",
  add: "Add an investment",
  convention:
    "Total cost = amount invested + fees. Total return = value at the end + income received. ROI = (total return − total cost) ÷ total cost. Annualized ROI = (total return ÷ total cost)^(1 ÷ years) − 1.",
  resultsHeading: "Results",
  netProfit: "Net profit",
  roi: "ROI",
  annualized: "Annualized ROI",
  totalCost: "Total cost",
  totalReturn: "Total return",
  rank: "Rank",
  investment: "Investment",
  noAnnualized: "—",
  rankNote:
    "Ranked by annualized ROI, which puts different holding periods on the same footing.",
  rankNoteRoi:
    "Ranked by plain ROI, because at least one investment has no holding period.",
  shortHold:
    "An annualized figure for a holding period under one year extrapolates a short result, so treat it with care.",
  workingHeading: "The working",
  workRoi: "{name}: ROI = ({ret} − {cost}) ÷ {cost} = {roi}",
  workAnnual: "Annualized = ({ret} ÷ {cost})^(1 ÷ {years}) − 1 = {annual}",
  chartTitle: "ROI and annualized ROI",
  chartXTitle: "Investment",
  chartYTitle: "Percent",
  chartLabel: "ROI comparison chart",
  chartDescription:
    "Bar chart of ROI and annualized ROI, in percent, for {count} investments.",
  seriesRoi: "ROI (%)",
  seriesAnnual: "Annualized ROI (%)",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  needData:
    "Enter an amount invested above zero and the value at the end for at least one investment.",
} as const;

type Strings = typeof ROI_CALCULATOR_STRINGS;

interface Row {
  id: number;
  name: string;
  invested: string;
  finalValue: string;
  income: string;
  costs: string;
  years: string;
}

let nextId = 0;

function makeRow(values: Partial<Omit<Row, "id">>): Row {
  nextId += 1;
  return {
    id: nextId,
    name: "",
    invested: "",
    finalValue: "",
    income: "",
    costs: "",
    years: "",
    ...values,
  };
}

const MAX_ROWS = 6;

interface Entry {
  name: string;
  result: RoiResult;
  years: number;
}

function toEntries(rows: Row[], defaultName: (n: number) => string): Entry[] {
  const entries: Entry[] = [];
  for (const [index, row] of rows.entries()) {
    const invested = parseNumberInput(row.invested);
    const finalValue = parseNumberInput(row.finalValue);
    if (invested === null || finalValue === null || invested <= 0) {
      continue;
    }
    const years = Math.max(0, parseNumberOr(row.years, 0));
    const result = computeRoi({
      invested,
      finalValue,
      income: parseNumberOr(row.income, 0),
      costs: Math.max(0, parseNumberOr(row.costs, 0)),
      years,
    });
    if (result) {
      entries.push({
        name: row.name.trim() || defaultName(index + 1),
        result,
        years,
      });
    }
  }
  return entries;
}

function Comparison({
  entries,
  currency,
  strings,
}: {
  entries: Entry[];
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 2 })}%`;
  const ranks = rankRois(entries.map((e) => e.result));
  const useAnnual = entries.every((e) => e.result.annualized !== null);
  const annual = (e: Entry) =>
    e.result.annualized === null
      ? strings.noAnnualized
      : pct(e.result.annualized);
  return (
    <>
      <DataTable
        head={[
          strings.rank,
          strings.investment,
          strings.totalCost,
          strings.totalReturn,
          strings.netProfit,
          strings.roi,
          strings.annualized,
        ]}
        rows={entries.map((e, i) => [
          fmt(ranks[i]),
          e.name,
          money(e.result.totalCost),
          money(e.result.totalReturn),
          money(e.result.netProfit),
          pct(e.result.roi),
          annual(e),
        ])}
      />
      {entries.length > 1 ? (
        <p className={HINT}>
          {useAnnual ? strings.rankNote : strings.rankNoteRoi}
        </p>
      ) : null}
    </>
  );
}

function RoiChart({
  entries,
  strings,
}: {
  entries: Entry[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const pctValue = (v: number | null) =>
    v === null ? null : Math.round(v * 10_000) / 100;
  const categories: BarCategory[] = entries.map((e) => ({
    label: e.name,
    values: [pctValue(e.result.roi), pctValue(e.result.annualized)],
  }));
  const names = [strings.seriesRoi, strings.seriesAnnual];
  const style = {
    title: strings.chartTitle,
    xTitle: strings.chartXTitle,
    yTitle: strings.chartYTitle,
    color: "blue" as const,
    valueLabels: true,
    apa: false,
    figureNumber: "1",
    note: "",
  };
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.investment, ...names],
        ...categories.map((c) => [c.label, ...c.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(strings.chartTitle, "roi-comparison")}
      labels={strings}
      note=""
      table={null}
      title={strings.chartTitle}
    >
      <BarChart
        categories={categories}
        description={t(strings.chartDescription, {
          count: fmt(entries.length),
        })}
        fmt={fmt}
        id="roi-chart"
        label={strings.chartLabel}
        mode="grouped"
        names={names}
        orientation="vertical"
        style={style}
      />
    </ChartFigure>
  );
}

function Working({
  entries,
  currency,
  strings,
}: {
  entries: Entry[];
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const num = (v: number) => fmt(v, { maximumFractionDigits: 6 });
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 4 })}%`;
  const lines = entries.flatMap((e) => {
    const values = {
      name: e.name,
      ret: money(e.result.totalReturn),
      cost: money(e.result.totalCost),
      roi: pct(e.result.roi),
      years: num(e.years),
      annual: e.result.annualized === null ? "" : pct(e.result.annualized),
    };
    return [
      t(strings.workRoi, values),
      ...(e.result.annualized === null ? [] : [t(strings.workAnnual, values)]),
    ];
  });
  return (
    <div className={`${CARD} space-y-1`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {lines.map((line) => (
        <p
          className="font-mono text-muted-foreground text-sm leading-relaxed"
          key={line}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function Results({
  entries,
  currency,
  strings,
}: {
  entries: Entry[];
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 2 })}%`;
  const first = entries[0];
  const shortHold = entries.some((e) => e.years > 0 && e.years < 1);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      {entries.length === 1 ? (
        <StatGrid>
          <StatCard
            emphasis
            label={strings.netProfit}
            value={money(first.result.netProfit)}
          />
          <StatCard label={strings.roi} value={pct(first.result.roi)} />
          <StatCard
            label={strings.annualized}
            value={
              first.result.annualized === null
                ? strings.noAnnualized
                : pct(first.result.annualized)
            }
          />
        </StatGrid>
      ) : null}
      <Comparison currency={currency} entries={entries} strings={strings} />
      {shortHold ? <Notice>{strings.shortHold}</Notice> : null}
      {entries.length > 1 ? (
        <RoiChart entries={entries} strings={strings} />
      ) : null}
      <Working currency={currency} entries={entries} strings={strings} />
    </section>
  );
}

export default function RoiCalculatorWidget() {
  const strings = useToolUi(ROI_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [currency, setCurrency] = useState("USD");
  const [rows, setRows] = useState<Row[]>(() => [
    makeRow({ invested: "10000", finalValue: "13500", years: "3" }),
    makeRow({
      invested: "5000",
      finalValue: "5600",
      income: "150",
      costs: "25",
      years: "1",
    }),
  ]);
  const patch = (id: number, change: Partial<Row>) =>
    setRows((old) =>
      old.map((row) => (row.id === id ? { ...row, ...change } : row))
    );
  const defaultName = (n: number) => t(strings.namePlaceholder, { n: fmt(n) });
  const entries = toEntries(rows, defaultName);

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        <div className="max-w-xs">
          <CurrencyField
            id="roi-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
        </div>
        {rows.map((row, index) => (
          <div
            className="space-y-3 rounded-lg border border-border p-4"
            key={row.id}
          >
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <TextField
                  id={`roi-name-${row.id}`}
                  label={t(strings.nameLabel, { n: fmt(index + 1) })}
                  onChange={(value) => patch(row.id, { name: value })}
                  placeholder={defaultName(index + 1)}
                  value={row.name}
                />
              </div>
              <button
                aria-label={t(strings.removeLabel, { n: fmt(index + 1) })}
                className={BUTTON_SMALL}
                disabled={rows.length <= 1}
                onClick={() =>
                  setRows((old) => old.filter((entry) => entry.id !== row.id))
                }
                type="button"
              >
                {strings.remove}
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <TextField
                id={`roi-invested-${row.id}`}
                label={strings.invested}
                numeric
                onChange={(value) => patch(row.id, { invested: value })}
                value={row.invested}
              />
              <TextField
                id={`roi-final-${row.id}`}
                label={strings.finalValue}
                numeric
                onChange={(value) => patch(row.id, { finalValue: value })}
                value={row.finalValue}
              />
              <TextField
                id={`roi-income-${row.id}`}
                label={strings.income}
                numeric
                onChange={(value) => patch(row.id, { income: value })}
                value={row.income}
              />
              <TextField
                id={`roi-costs-${row.id}`}
                label={strings.costs}
                numeric
                onChange={(value) => patch(row.id, { costs: value })}
                value={row.costs}
              />
              <TextField
                id={`roi-years-${row.id}`}
                label={strings.years}
                numeric
                onChange={(value) => patch(row.id, { years: value })}
                value={row.years}
              />
            </div>
          </div>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={rows.length >= MAX_ROWS}
          onClick={() => setRows((old) => [...old, makeRow({})])}
          type="button"
        >
          {strings.add}
        </button>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {entries.length > 0 ? (
        <Results currency={currency} entries={entries} strings={strings} />
      ) : (
        <Notice>{strings.needData}</Notice>
      )}
    </div>
  );
}
